/**
 * ProcessGraph — Interactive read-only flow graph for visualizing a Plan's
 * phases, steps, and dependency edges using ReactFlow + dagre layout.
 *
 * Phases rendered as labeled group nodes (swim lanes).
 * Steps rendered as card nodes with status icon and optional role badge.
 * Dependency edges drawn from step.dependsOn arrays.
 * Read-only: pan + zoom only, no drag/edit. Click a node for detail popover.
 */

import React, { useMemo, useCallback, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  type Node,
  type Edge,
  type NodeTypes,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import {
  Box,
  Typography,
  Chip,
  Popover,
  Paper,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BlockIcon from '@mui/icons-material/Block';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { type Plan, type PlanStep, type PlanPhase, StepStatus } from '../types/generated/graphql';
import { colors } from '../theme/colors';

// ─── Constants ───────────────────────────────────────────────────────────────

const NODE_WIDTH = 220;
const NODE_HEIGHT = 56;
const GROUP_PADDING = { top: 48, right: 24, bottom: 24, left: 24 };
const PHASE_GAP = 40;

// Phase color palette (cycled)
const PHASE_COLORS = [
  { bg: '#EFF6FF', border: '#BFDBFE', label: colors.blue[700] },
  { bg: '#F0FDF4', border: '#BBF7D0', label: colors.green[700] },
  { bg: '#FEF3C7', border: '#FDE68A', label: colors.amber[700] },
  { bg: '#FAF5FF', border: '#E9D5FF', label: '#7C3AED' },
  { bg: '#FFF7ED', border: '#FFEDD5', label: colors.orange[700] },
];

// ─── Step status helpers ─────────────────────────────────────────────────────

function statusIcon(status: StepStatus) {
  const size = 16;
  switch (status) {
    case StepStatus.Completed:
      return <CheckCircleIcon sx={{ fontSize: size, color: colors.green[600] }} />;
    case StepStatus.InProgress:
      return <HourglassBottomIcon sx={{ fontSize: size, color: colors.blue[600] }} />;
    case StepStatus.WaitingExternal:
      return <HourglassBottomIcon sx={{ fontSize: size, color: colors.amber[600] }} />;
    case StepStatus.Blocked:
      return <BlockIcon sx={{ fontSize: size, color: colors.red[600] }} />;
    case StepStatus.Skipped:
      return <SkipNextIcon sx={{ fontSize: size, color: colors.gray[400] }} />;
    default:
      return <RadioButtonUncheckedIcon sx={{ fontSize: size, color: colors.gray[400] }} />;
  }
}

function statusLabel(status: StepStatus): string {
  switch (status) {
    case StepStatus.Completed: return 'Completed';
    case StepStatus.InProgress: return 'In Progress';
    case StepStatus.WaitingExternal: return 'Waiting for response';
    case StepStatus.Blocked: return 'Blocked';
    case StepStatus.Skipped: return 'Skipped';
    default: return 'Not Started';
  }
}

// ─── Custom node component ──────────────────────────────────────────────────

interface StepNodeData {
  label: string;
  status: StepStatus;
  roleName?: string;
  description: string;
  phaseTitle: string;
  [key: string]: unknown;
}

function StepNode({ data }: { data: StepNodeData }) {
  return (
    <Box
      sx={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: colors.gray[200],
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': {
          borderColor: colors.blue[400],
          boxShadow: `0 2px 8px ${colors.blue[100]}`,
        },
      }}
    >
      <Handle type="target" position={Position.Top} style={{ visibility: 'hidden', width: 0, height: 0 }} />
      {statusIcon(data.status)}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: 500,
          fontSize: '0.75rem',
          lineHeight: 1.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {data.label}
      </Typography>
      <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden', width: 0, height: 0 }} />
    </Box>
  );
}

// ─── Phase group node ────────────────────────────────────────────────────────

interface PhaseGroupData {
  label: string;
  colorIdx: number;
  [key: string]: unknown;
}

function PhaseGroupNode({ data }: { data: PhaseGroupData }) {
  const palette = PHASE_COLORS[data.colorIdx % PHASE_COLORS.length];
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: palette.bg,
        border: 1,
        borderColor: palette.border,
        borderRadius: 2,
        position: 'relative',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          top: 12,
          left: 16,
          fontWeight: 700,
          color: palette.label,
          fontSize: '0.7rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
      >
        {data.label}
      </Typography>
    </Box>
  );
}

const nodeTypes: NodeTypes = {
  stepNode: StepNode as any,
  phaseGroup: PhaseGroupNode as any,
};

// ─── Layout with dagre ──────────────────────────────────────────────────────

function layoutGraph(
  plan: Plan,
): { nodes: Node[]; edges: Edge[] } {
  const sortedPhases = [...plan.phases].sort((a, b) => a.order - b.order);
  const phaseMap = new Map<string, PlanPhase>();
  sortedPhases.forEach((p) => phaseMap.set(p.id, p));

  // Group steps by phase
  const stepsByPhase = new Map<string, PlanStep[]>();
  sortedPhases.forEach((p) => stepsByPhase.set(p.id, []));
  plan.steps.forEach((s) => {
    const arr = stepsByPhase.get(s.phaseId);
    if (arr) arr.push(s);
  });

  // Role lookup
  const roleMap = new Map<string, string>();
  (plan.roles || []).forEach((r) => roleMap.set(r.roleId, r.name));

  // Build dagre graph per phase for intra-phase layout
  const allNodes: Node[] = [];
  const allEdges: Edge[] = [];

  // Track cumulative Y offset for phases laid out top-to-bottom
  let phaseYOffset = 0;

  sortedPhases.forEach((phase, phaseIdx) => {
    const steps = stepsByPhase.get(phase.id) || [];
    if (steps.length === 0) return;

    // Create dagre graph for this phase
    const g = new dagre.graphlib.Graph();
    g.setGraph({
      rankdir: 'TB',
      nodesep: 20,
      ranksep: 60,
      marginx: 0,
      marginy: 0,
    });
    g.setDefaultEdgeLabel(() => ({}));

    // Add step nodes
    steps.forEach((step) => {
      g.setNode(step.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
    });

    // Add intra-phase edges (dependencies within the same phase)
    const phaseStepIds = new Set(steps.map((s) => s.id));
    steps.forEach((step) => {
      step.dependsOn.forEach((depId) => {
        if (phaseStepIds.has(depId)) {
          g.setEdge(depId, step.id);
        }
      });
    });

    dagre.layout(g);

    // Calculate phase group bounds
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    steps.forEach((step) => {
      const n = g.node(step.id);
      if (!n) return;
      minX = Math.min(minX, n.x - NODE_WIDTH / 2);
      minY = Math.min(minY, n.y - NODE_HEIGHT / 2);
      maxX = Math.max(maxX, n.x + NODE_WIDTH / 2);
      maxY = Math.max(maxY, n.y + NODE_HEIGHT / 2);
    });

    const phaseWidth = (maxX - minX) + GROUP_PADDING.left + GROUP_PADDING.right;
    const phaseHeight = (maxY - minY) + GROUP_PADDING.top + GROUP_PADDING.bottom;

    // Phase group node
    const phaseNodeId = `phase-group-${phase.id}`;
    allNodes.push({
      id: phaseNodeId,
      type: 'phaseGroup',
      position: { x: 0, y: phaseYOffset },
      data: { label: phase.title, colorIdx: phaseIdx },
      style: { width: phaseWidth, height: phaseHeight },
      selectable: false,
      draggable: false,
    });

    // Step nodes positioned within phase group
    steps.forEach((step) => {
      const n = g.node(step.id);
      if (!n) return;
      allNodes.push({
        id: step.id,
        type: 'stepNode',
        position: {
          x: (n.x - minX) + GROUP_PADDING.left - NODE_WIDTH / 2,
          y: (n.y - minY) + GROUP_PADDING.top - NODE_HEIGHT / 2,
        },
        parentId: phaseNodeId,
        extent: 'parent' as const,
        data: {
          label: step.title,
          status: step.status,
          roleName: step.assignedRoleId ? roleMap.get(step.assignedRoleId) : undefined,
          description: step.description,
          phaseTitle: phase.title,
        },
        draggable: false,
      });
    });

    phaseYOffset += phaseHeight + PHASE_GAP;
  });

  // All dependency edges (both intra-phase and cross-phase)
  const stepNodeIds = new Set(allNodes.filter((n) => n.type === 'stepNode').map((n) => n.id));
  plan.steps.forEach((step) => {
    step.dependsOn.forEach((depId) => {
      if (stepNodeIds.has(depId) && stepNodeIds.has(step.id)) {
        allEdges.push({
          id: `e-${depId}-${step.id}`,
          source: depId,
          target: step.id,
          type: 'smoothstep',
          animated: false,
          style: { stroke: colors.gray[300], strokeWidth: 1.5 },
        });
      }
    });
  });

  return { nodes: allNodes, edges: allEdges };
}

// ─── Detail popover on click ────────────────────────────────────────────────

interface StepDetailPopoverProps {
  anchorEl: HTMLElement | null;
  step: StepNodeData | null;
  onClose: () => void;
}

function StepDetailPopover({ anchorEl, step, onClose }: StepDetailPopoverProps) {
  if (!step) return null;
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{ paper: { sx: { maxWidth: 340, p: 2, borderRadius: 2 } } }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          {statusIcon(step.status)}
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {step.label}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {step.description}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={statusLabel(step.status)} size="small" variant="outlined" />
          {step.roleName && <Chip label={step.roleName} size="small" variant="outlined" />}
          <Chip label={step.phaseTitle} size="small" variant="outlined" />
        </Stack>
      </Stack>
    </Popover>
  );
}

// ─── Inner flow (needs ReactFlowProvider) ───────────────────────────────────

function ProcessGraphInner({ plan }: { plan: Plan }) {
  const { nodes, edges } = useMemo(() => layoutGraph(plan), [plan]);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedStep, setSelectedStep] = useState<StepNodeData | null>(null);
  const { fitView } = useReactFlow();

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    if (node.type !== 'stepNode') return;
    setSelectedStep(node.data as StepNodeData);
    setPopoverAnchor(_.currentTarget as HTMLElement);
  }, []);

  const handleClosePopover = useCallback(() => {
    setPopoverAnchor(null);
    setSelectedStep(null);
  }, []);

  // Fit view on mount
  const onInit = useCallback(() => {
    setTimeout(() => fitView({ padding: 0.15 }), 50);
  }, [fitView]);

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        onInit={onInit}
        fitView
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnScroll
        zoomOnScroll
        style={{ background: colors.background }}
      >
        <Background gap={20} size={1} color={colors.gray[200]} />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(n) => {
            if (n.type === 'phaseGroup') {
              const idx = (n.data as PhaseGroupData).colorIdx ?? 0;
              return PHASE_COLORS[idx % PHASE_COLORS.length].border;
            }
            return colors.gray[200];
          }}
          maskColor="rgba(0,0,0,0.08)"
          style={{ borderRadius: 8 }}
        />
      </ReactFlow>
      <StepDetailPopover
        anchorEl={popoverAnchor}
        step={selectedStep}
        onClose={handleClosePopover}
      />
    </>
  );
}

// ─── Exported wrapper ───────────────────────────────────────────────────────

interface ProcessGraphProps {
  plan: Plan;
  height?: number | string;
}

export const ProcessGraph: React.FC<ProcessGraphProps> = ({ plan, height = 420 }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        height,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <ReactFlowProvider>
        <ProcessGraphInner plan={plan} />
      </ReactFlowProvider>
    </Paper>
  );
};

export default ProcessGraph;
